// @flow
import { Button } from "antd";
import map from "lodash/map";
import * as React from "react";
import tw from "tailwind-styled-components";
import Checkbox from "ui/atoms/Checkbox";
import FlexBox from "ui/atoms/FlexBox";
import Space from "ui/atoms/Space";
import Tooltip from "ui/atoms/Tooltip";
import Icons, { Details } from "ui/Icons";

import cn from "@/utils/tailwind/cn";

import BuilderChoiceExtraProp from "./BuilderChoiceExtraProp";

const BaseField = tw.input`
  form-input
  focus:!border-primary
  focus:shadow-[0_0_0_2px_rgba(5,130,255,0.08)]
  border
  rounded-md
  transition
  duration-150
  ease-in-out
  text-gray-700
  sm:text-sm
  sm:leading-5
`;

const ValueInput = tw(BaseField)`
  ${(props: { assignValues: ?boolean }) =>
    props.assignValues
      ? `
    w-1/2
    md:w-2/3
    xl:w-3/4
    rounded-r-none
    border-r-0
  `
      : `
    w-full
  `}

`;

const NumberValueInput = tw(BaseField)`
  w-1/2
  md:w-1/3
  xl:w-1/4
  rounded-l-none
`;

type Props = {
  id: string,
  index: number,
  value: string,
  requestDetails?: boolean,
  onUpdateValue: (id: string, value: string) => void,
  onUpdateNumberValue?: (id: string, event: string) => void,
  onRemove: (id: string) => void,
  onToggleRequestDetails?: (id: string, value: boolean) => void,
  mayRequestDetails?: ?boolean,
  numberValue?: number,
  assignValues?: boolean,
  includeDefaultValues?: boolean,
  defaultValue?: ?string,
  onUpdateDefaultValues?: (optionId: string, allowMultiple?: boolean) => void,
  extraInputs?: Array<{
    placeholder: string,
    type: string,
    value: string,
    onChange: (id: string, value: string) => void,
  }>,
};

function BuilderChoiceProp({
  id,
  value,
  requestDetails,
  onUpdateValue,
  onRemove,
  onToggleRequestDetails,
  mayRequestDetails,
  extraInputs = [],
  numberValue,
  assignValues,
  onUpdateNumberValue,
  includeDefaultValues,
  onUpdateDefaultValues,
  defaultValue,
}: Props): React.Node {
  const [inputValue, setInputValue] = React.useState(value);
  const [numberInputValue, setNumberInputValue] = React.useState(numberValue);

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  React.useEffect(() => {
    setNumberInputValue(numberValue);
  }, [numberValue]);

  const handleUpdateNumberValue = React.useCallback(
    (event: SyntheticEvent<HTMLInputElement>) => {
      const value = event.currentTarget.value;

      if (isNaN(value)) {
        return;
      }

      if (onUpdateNumberValue) {
        onUpdateNumberValue(id, value);
      }

      setNumberInputValue(value);
    },
    [id, onUpdateNumberValue],
  );

  const handleInputChange = (event: SyntheticEvent<HTMLInputElement>) => {
    setInputValue(event.currentTarget.value);
    onUpdateValue(id, event.currentTarget.value);
  };

  const showDefaultValueCheckbox =
    includeDefaultValues && onUpdateDefaultValues;

  const defaultValues = (defaultValue || "").split(",");

  return (
    <div className="w-full flex gap-2 items-center content-between py-1 pr-1">
      <Icons.KebabMenu />
      {showDefaultValueCheckbox && (
        <Checkbox
          checked={defaultValues.includes(id.toString())}
          className="mx-2 hidden"
          key={`checkbox-${id}-default-${defaultValue || ""}`} // workaround to fix a rendering inssue inside a modal
          onChange={() =>
            onUpdateDefaultValues ? onUpdateDefaultValues(id.toString()) : null
          }
        />
      )}
      <FlexBox>
        <Space size="xsmall">
          <div>
            <ValueInput
              assignValues={assignValues}
              onChange={handleInputChange}
              placeholder="Option"
              value={inputValue}
            />
            {assignValues && (
              <NumberValueInput
                className="!border-l-0 text-center"
                onChange={handleUpdateNumberValue}
                placeholder="#"
                value={isNaN(numberInputValue) ? "" : numberInputValue}
              />
            )}
          </div>
          {map(extraInputs, ({ placeholder, type, value, onChange }) => (
            <BuilderChoiceExtraProp
              id={id}
              key={placeholder}
              onChange={onChange}
              placeholder={placeholder}
              type={type}
              value={value}
            />
          ))}
        </Space>
      </FlexBox>
      {mayRequestDetails && (
        <Tooltip label="Request details" mouseEnterDelay={0.4}>
          <Button
            className={cn("h-8 w-8 p-1 rounded", {
              "bg-primary": requestDetails,
            })}
            onClick={() => onToggleRequestDetails?.(id, !requestDetails)}
            type="text"
          >
            <Details
              className={cn({
                "!fill-white": requestDetails,
              })}
            />
          </Button>
        </Tooltip>
      )}
      <Tooltip label="Remove" mouseEnterDelay={0.4}>
        <Button
          className="h-8 w-8 p-1 rounded"
          onClick={() => onRemove(id)}
          type="text"
        >
          <Icons.X />
        </Button>
      </Tooltip>
    </div>
  );
}

export default BuilderChoiceProp;
